<template>
  <woot-modal :show.sync="show" :on-close="onClose" size="modal-medium">
    <woot-modal-header
      :header-title="stepTitle"
      :header-content="stepDescription"
    />

    <form class="opportunity-form" @submit.prevent="handleSubmit">
      <div class="steps-indicator">
        <div
          :class="[
            'step',
            { active: currentStep === 1, completed: currentStep > 1 },
          ]"
        >
          1
        </div>
        <div :class="['line', { completed: currentStep > 1 }]" />
        <div
          :class="[
            'step',
            { active: currentStep === 2, completed: currentStep > 2 },
          ]"
        >
          2
        </div>
        <template v-if="hasNegotiationStep">
          <div :class="['line', { completed: currentStep > 2 }]" />
          <div :class="['step', { active: currentStep === 3 }]">
            3
          </div>
        </template>
      </div>

      <template v-if="currentStep === 1">
        <div class="row">
          <div class="columns" :class="{ errorSelect: errors.pipelineId }">
            <label>
              {{ $t('OPPORTUNITY_DETAILS.CREATE_MODAL.FUNNEL') }}
            </label>
            <multiselect
              v-model="form.pipelineId"
              track-by="id"
              label="name"
              :placeholder="
                $t('OPPORTUNITY_DETAILS.CREATE_MODAL.SELECT_PIPELINE')
              "
              :multiple="false"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :close-on-select="true"
              :options="filteredPipelines"
              :allow-empty="false"
              @select="getPipelineStages"
            >
              <template v-slot:noResult>
                {{ $t('OPPORTUNITY_DETAILS.CREATE_MODAL.NOT_PIPELINE_FOUND') }}
              </template>
            </multiselect>
            <span v-if="errors.pipelineId" class="message">{{
              errors.pipelineId
            }}</span>
          </div>
        </div>

        <div class="row">
          <div class="columns">
            <label :class="{ error: errors.name }">
              {{ $t('OPPORTUNITY_DETAILS.CREATE_MODAL.NAME') }}
              <input
                v-model.trim="form.name"
                type="text"
                name="name"
                @input="onNameInput"
                @change="clearError('name')"
              />
              <span v-if="errors.name" class="message">{{ errors.name }}</span>
            </label>
          </div>
        </div>

        <div class="row">
          <div class="columns">
            <label :class="{ error: errors.description }">
              {{ $t('OPPORTUNITY_DETAILS.CREATE_MODAL.DESCRIPTION') }}
              <textarea
                v-model.trim="form.description"
                rows="4"
                @input="onDescriptionInput"
                @change="clearError('description')"
              />
              <span v-if="errors.description" class="message">{{
                errors.description
              }}</span>
            </label>
          </div>
        </div>
      </template>

      <template v-else-if="currentStep === 2">
        <div class="row">
          <div class="columns" :class="{ errorSelect: errors.pipelineStageId }">
            <label>
              {{ $t('OPPORTUNITY_DETAILS.CREATE_MODAL.STAGE') }}
            </label>
            <multiselect
              v-model="form.pipelineStageId"
              track-by="id"
              label="name"
              :placeholder="$t('OPPORTUNITY_DETAILS.CREATE_MODAL.SELECT_STAGE')"
              :multiple="false"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :close-on-select="true"
              :options="stages"
              :allow-empty="false"
              @select="clearError('pipelineStageId')"
            >
              <template v-slot:noResult>
                {{ $t('OPPORTUNITY_DETAILS.CREATE_MODAL.NOT_PIPELINE_FOUND') }}
              </template>
            </multiselect>
            <span v-if="errors.pipelineStageId" class="message">{{
              errors.pipelineStageId
            }}</span>
          </div>
        </div>

        <div class="row">
          <div class="columns">
            <div class="value-field">
              <label :class="{ error: errors.currency }">
                {{ $t('OPPORTUNITY_DETAILS.CREATE_MODAL.CURRENCY') }}
                <select
                  v-model="form.currency"
                  :disabled="supportedCurrencies.length === 1"
                  @change="clearError('currency')"
                >
                  <option
                    v-for="currency in supportedCurrencies"
                    :key="currency"
                    :value="currency"
                  >
                    {{ currency }}
                  </option>
                </select>
                <span v-if="errors.currency" class="message">{{
                  errors.currency
                }}</span>
              </label>
              <label :class="{ error: errors.value }" class="value-input">
                {{ $t('OPPORTUNITY_DETAILS.CREATE_MODAL.VALUE') }}
                <decimal-input
                  v-model="form.value"
                  @change="clearError('value')"
                />
                <span v-if="errors.value" class="message">{{
                  errors.value
                }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="columns">
            <opportunity-temperature-select
              v-model="form.temperature"
              class="temperature-field"
            />
          </div>
        </div>

        <div class="row">
          <div class="columns">
            <label>
              {{ $t('OPPORTUNITY_DETAILS.CREATE_MODAL.PRODUCTS') }}
            </label>
            <opportunity-products-select
              :selected-products="selectedProductLabels"
              :available-product-labels="availableProductLabels"
              @add="addProductLabelToOpportunity"
              @remove="removeProductLabelFromOpportunity"
            />
          </div>
        </div>
      </template>

      <opportunity-negotiation-fields
        v-else
        v-model="negotiationAnswers"
        :questions="negotiationQuestions"
        :required-question-ids="requiredNegotiationQuestionIds"
        :errors="negotiationErrors"
        @field-change="clearNegotiationError"
      />

      <div class="modal-actions">
        <woot-button
          v-if="currentStep === 1"
          type="button"
          class="button clear"
          @click.prevent="onClose"
        >
          {{ $t('OPPORTUNITY_DETAILS.CREATE_MODAL.CANCEL') }}
        </woot-button>
        <woot-button
          v-if="currentStep > 1"
          type="button"
          class="button clear"
          @click.prevent="goToPreviousStep"
        >
          {{ $t('OPPORTUNITY_DETAILS.CREATE_MODAL.PREVIOUS') }}
        </woot-button>

        <woot-button
          v-if="currentStep < totalSteps"
          class="next-step"
          @click.prevent="goToNextStep"
        >
          {{ $t('OPPORTUNITY_DETAILS.CREATE_MODAL.NEXT') }}
        </woot-button>
        <woot-submit-button
          v-else
          :loading="
            uiFlags.isCreatingOpportunity ||
              uiFlags.isCreatingProduct ||
              isLoadingNegotiationForm
          "
          :button-text="$t('OPPORTUNITY_DETAILS.CREATE_MODAL.SAVE')"
        />
      </div>
    </form>
  </woot-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import { SUPPORTED_CURRENCIES } from '../settings/opportunityPipelines/create/config';
import OpportunityProductsSelect from '../opportunities/components/OpportunityProductsSelect';
import OpportunityTemperatureSelect from '../opportunities/components/OpportunityTemperatureSelect.vue';
import { DEFAULT_OPPORTUNITY_TEMPERATURE } from '../../../helper/opportunityTemperatureHelper';
import { sortStagesByPosition } from '../../../helper/opportunityPipelineHelper';
import DecimalInput from '../../../components/widgets/forms/DecimalInput.vue';
import OpportunityNegotiationFields from '../opportunities/components/negotiationForm/OpportunityNegotiationFields.vue';
import {
  buildAnswersForm,
  buildAnswersPayload,
  hasActiveNegotiationForm,
  invalidAnswerQuestions,
  isQuestionRequiredAt,
  pendingQuestionIdsFromError,
  pendingRequiredQuestions,
} from '../../../helper/opportunityNegotiationFormHelper';

export default {
  components: {
    DecimalInput,
    OpportunityNegotiationFields,
    OpportunityProductsSelect,
    OpportunityTemperatureSelect,
  },
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: true,
    },
    currentChat: {
      type: Object,
      default: () => ({}),
    },
    pipelines: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentStep: 1,
      stages: [],
      errors: {},
      nameEdited: false,
      descriptionEdited: false,
      negotiationQuestions: [],
      negotiationAnswers: {},
      negotiationErrors: {},
      negotiationFormRequest: null,
      negotiationFormRequestId: 0,
      isLoadingNegotiationForm: false,
      form: {
        pipelineId: '',
        name: '',
        description: '',
        pipelineStageId: '',
        currency: '',
        value: '',
        temperature: DEFAULT_OPPORTUNITY_TEMPERATURE,
        productLabelIds: [],
      },
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      accountProductLabels: 'productLabels/getProductLabels',
      uiFlags: 'opportunities/getUIFlags',
    }),
    filteredPipelines() {
      if (!this.pipelines || !this.pipelines.length) return [];
      if (!this.currentChat || !this.currentChat.inbox_id)
        return this.pipelines;

      return this.pipelines.filter(pipeline => {
        const matchesInbox =
          pipeline.inbox_ids &&
          pipeline.inbox_ids.includes(this.currentChat.inbox_id);
        const matchesUser =
          this.currentUser && pipeline.user_ids
            ? pipeline.user_ids.includes(this.currentUser.id)
            : true;
        const activePipeline = pipeline.status === 'active';

        return matchesInbox && matchesUser && activePipeline;
      });
    },
    hasNegotiationStep() {
      return this.negotiationQuestions.length > 0;
    },
    totalSteps() {
      return this.hasNegotiationStep ? 3 : 2;
    },
    requiredNegotiationQuestionIds() {
      return this.negotiationQuestions
        .filter(question =>
          isQuestionRequiredAt(question, this.form.pipelineStageId)
        )
        .map(question => question.id);
    },
    stepTitle() {
      return this.$t(
        `OPPORTUNITY_DETAILS.CREATE_MODAL.TITLE_STEP${this.currentStep}`
      );
    },
    stepDescription() {
      return this.$t(
        `OPPORTUNITY_DETAILS.CREATE_MODAL.SUBTITLE_STEP${this.currentStep}`
      );
    },
    supportedCurrencies() {
      return SUPPORTED_CURRENCIES;
    },
    availableProductLabels() {
      return this.accountProductLabels.filter(
        productLabel => !this.form.productLabelIds.includes(productLabel.id)
      );
    },
    selectedProductLabels() {
      return this.accountProductLabels.filter(productLabel =>
        this.form.productLabelIds.includes(productLabel.id)
      );
    },
    contactName() {
      return this.currentChat.meta?.sender?.name || '';
    },
  },
  watch: {
    filteredPipelines() {
      this.selectDefaultPipeline();
    },
  },
  mounted() {
    this.selectDefaultPipeline();
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    selectDefaultPipeline() {
      if (this.form.pipelineId || !this.filteredPipelines.length) return;
      const [firstAccessiblePipeline] = this.filteredPipelines;
      this.form.pipelineId = firstAccessiblePipeline;
      this.getPipelineStages(firstAccessiblePipeline);
    },
    onNameInput() {
      this.nameEdited = true;
    },
    onDescriptionInput() {
      this.descriptionEdited = true;
    },
    applyDefaultFormValues(pipeline) {
      if (!this.nameEdited) {
        this.form.name = [this.contactName, pipeline.name]
          .filter(Boolean)
          .join(' | ');
      }
      if (!this.descriptionEdited) {
        this.form.description = this.$t(
          'OPPORTUNITY_DETAILS.CREATE_MODAL.DEFAULT_DESCRIPTION',
          { contactName: this.contactName, conversationId: this.currentChat.id }
        );
      }
    },
    validateStep(stepNumber) {
      this.errors = {};

      const rules = {
        1: [
          { field: 'pipelineId', errorKey: 'PIPELINE_REQUIRED' },
          { field: 'name', errorKey: 'NAME_REQUIRED' },
          { field: 'description', errorKey: 'DESCRIPTION_REQUIRED' },
        ],
        2: [
          { field: 'pipelineStageId', errorKey: 'STAGE_REQUIRED' },
          { field: 'value', errorKey: 'VALUE_REQUIRED' },
          { field: 'currency', errorKey: 'CURRENCY_REQUIRED' },
        ],
      };

      const currentRules = rules[stepNumber] || [];

      currentRules.forEach(({ field, errorKey }) => {
        if (!this.form[field]) {
          this.errors[field] = this.$t(
            `OPPORTUNITY_DETAILS.CREATE_MODAL.ERRORS.${errorKey}`
          );
        }
      });

      return Object.keys(this.errors).length === 0;
    },
    clearError(field) {
      if (this.errors[field]) {
        delete this.errors[field];
      }
    },
    async getPipelineStages(pipeline) {
      this.clearError('pipelineId');
      this.applyDefaultFormValues(pipeline);
      const negotiationFormRequest = this.loadNegotiationForm(pipeline);
      this.negotiationFormRequest = negotiationFormRequest;

      try {
        const stages = await this.$store.dispatch(
          'opportunityPipelines/getStages',
          pipeline.id
        );
        this.stages = sortStagesByPosition(stages);
        this.form.pipelineStageId = this.stages[0] || '';
        this.form.value = pipeline.default_value;
        this.form.currency = pipeline.default_currency;
      } catch (error) {
        this.stages = [];
      }
      await negotiationFormRequest;
    },
    isSelectedPipeline(pipeline) {
      return !!this.form.pipelineId && this.form.pipelineId.id === pipeline.id;
    },
    async loadNegotiationForm(pipeline) {
      this.negotiationFormRequestId += 1;
      const requestId = this.negotiationFormRequestId;
      this.isLoadingNegotiationForm = true;
      this.negotiationQuestions = [];
      this.negotiationAnswers = {};
      this.negotiationErrors = {};

      try {
        const currentPipeline = await this.$store.dispatch(
          'opportunityPipelines/show',
          pipeline.id
        );
        if (
          !this.isSelectedPipeline(pipeline) ||
          !hasActiveNegotiationForm(currentPipeline)
        ) {
          return;
        }
        await this.fetchNegotiationQuestions(pipeline);
      } catch (error) {
        this.negotiationQuestions = [];
      } finally {
        if (requestId === this.negotiationFormRequestId) {
          this.isLoadingNegotiationForm = false;
        }
      }
    },
    async fetchNegotiationQuestions(pipeline) {
      const negotiationForm = await this.$store.dispatch(
        'opportunityPipelines/getNegotiationForm',
        pipeline.id
      );
      if (!this.isSelectedPipeline(pipeline)) return;

      const questions =
        negotiationForm && negotiationForm.status === 'active'
          ? negotiationForm.questions || []
          : [];
      const answers = buildAnswersForm(questions);
      questions.forEach(question => {
        if (question.id in this.negotiationAnswers) {
          answers[question.id] = this.negotiationAnswers[question.id];
        }
      });
      this.negotiationQuestions = questions;
      this.negotiationAnswers = answers;
    },
    async openNegotiationStepOn(pendingQuestionIds) {
      const knownIds = this.negotiationQuestions.map(question => question.id);
      if (pendingQuestionIds.some(id => !knownIds.includes(id))) {
        try {
          await this.fetchNegotiationQuestions(this.form.pipelineId);
        } catch (error) {
          return;
        }
      }
      if (this.hasNegotiationStep) {
        this.showNegotiationErrors(pendingQuestionIds);
      }
    },
    validateNegotiationStep() {
      const answers = buildAnswersPayload(
        this.negotiationQuestions,
        this.negotiationAnswers
      );
      const errors = {};
      invalidAnswerQuestions(this.negotiationQuestions, answers).forEach(
        question => {
          errors[question.id] = this.$t(
            `OPPORTUNITY_DETAILS.NEGOTIATION_FORM.INVALID.${question.question_type}`
          );
        }
      );
      pendingRequiredQuestions(
        this.negotiationQuestions,
        this.form.pipelineStageId,
        answers
      ).forEach(question => {
        errors[question.id] =
          errors[question.id] ||
          this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.REQUIRED');
      });
      this.negotiationErrors = errors;

      return Object.keys(errors).length === 0;
    },
    goToPreviousStep() {
      this.currentStep = Math.max(this.currentStep - 1, 1);
    },
    goToNextStep() {
      if (!this.validateStep(this.currentStep)) {
        return;
      }

      this.currentStep += 1;
    },
    clearNegotiationError(questionId) {
      this.$delete(this.negotiationErrors, questionId);
    },
    showNegotiationErrors(questionIds) {
      this.negotiationErrors = questionIds.reduce((errors, id) => {
        errors[id] = this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.REQUIRED');
        return errors;
      }, {});
      this.currentStep = 3;
    },
    async handleSubmit() {
      await this.negotiationFormRequest;
      if (this.currentStep < this.totalSteps) {
        this.goToNextStep();
        return;
      }
      if (!this.validateStep(2)) {
        this.currentStep = 2;
        return;
      }
      if (this.hasNegotiationStep && !this.validateNegotiationStep()) {
        return;
      }

      try {
        const payload = {
          pipeline_id: this.form.pipelineId.id,
          pipeline_stage_id: this.form.pipelineStageId.id,
          name: this.form.name,
          description: this.form.description,
          currency: this.form.currency,
          value: this.form.value || undefined,
          temperature: this.form.temperature,
          conversation_id: this.currentChat.primary_key,
          status: 'open',
          owner_id: this.currentUser.id,
          contact_id: this.currentChat.meta?.sender?.id,
          product_label_ids: this.form.productLabelIds,
        };
        if (this.hasNegotiationStep) {
          payload.negotiation_form_answers = buildAnswersPayload(
            this.negotiationQuestions,
            this.negotiationAnswers
          );
        }

        await this.$store.dispatch('opportunities/create', payload);

        await this.$store.dispatch('opportunities/fetchByConversation', {
          conversationId: this.currentChat.primary_key,
        });

        window.bus.$emit('opportunity_created', {
          conversationId: this.currentChat.primary_key,
        });

        this.showAlert(this.$t('OPPORTUNITY_DETAILS.CREATE_MODAL.SUCCESS'));
        this.onClose();
      } catch (error) {
        const pendingQuestionIds = pendingQuestionIdsFromError(error);
        if (pendingQuestionIds.length) {
          await this.openNegotiationStepOn(pendingQuestionIds);
        }
        const backendMessage =
          error && error.response && error.response.data
            ? error.response.data.message
            : null;
        this.showAlert(
          backendMessage ||
            this.$t('OPPORTUNITY_DETAILS.CREATE_MODAL.ERRORS.CREATE_FAILED')
        );
      }
    },
    addProductLabelToOpportunity(productLabel) {
      if (!this.form.productLabelIds.includes(productLabel.id)) {
        this.form.productLabelIds.push(productLabel.id);
      }
    },
    removeProductLabelFromOpportunity(productLabel) {
      this.form.productLabelIds = this.form.productLabelIds.filter(
        id => id !== productLabel.id
      );
    },
  },
};
</script>

<style scoped lang="scss">
.opportunity-form {
  padding: var(--space-normal) var(--space-large) var(--space-large);
}

.steps-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 18px;
  margin-bottom: 28px;
}

.step {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #e5e5e5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
}

.line {
  width: 150px;
  height: 2px;
  background: #e5e5e5;
}

.step.active {
  background: var(--c-500);
  color: var(--v-50);
}

.step.completed {
  background: #22c55e;
  color: var(--v-50);
}

.line.completed {
  background: #22c55e;
}

.errorSelect .message {
  margin-bottom: 1rem;
}

.errorSelect ::v-deep .multiselect__tags {
  border: 1px solid var(--r-400);
}

.error textarea,
.error input[type='text'],
.errorSelect .multiselect {
  margin-bottom: 0;
}

.value-field {
  display: flex;
  gap: var(--space-small);
}

.value-input {
  flex: 1;
}

.temperature-field {
  margin-bottom: var(--space-normal);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-small);
  margin-top: var(--space-large);
}

.message {
  color: var(--r-700);
  font-size: var(--font-size-mini);
  display: block;
  margin-top: var(--space-smaller);
}
</style>
